import { createGlobalStyle } from 'styled-components';

// ============================================================
//  PandaTaps - "Bamboo Bento" design system
//  Warm ink-on-cream, disciplined bamboo green, editorial type.
//  Tokens are role-named so the whole site (and per-app accents)
//  stay consistent. Content & routes are unchanged - visuals only.
// ============================================================

const GlobalStyles = createGlobalStyle`
  :root {
    /* The site is light-only on purpose: many app pages are hand-built dark
       worlds, so a token-flip dark mode would only half-apply. */
    color-scheme: light;

    /* Core palette */
    --cream: #FAF8F2;          /* page background (warm, never #fff) */
    --surface: #FFFFFF;        /* raised cards */
    --paper-raised: #F4F1E8;   /* footer / alt sections */
    --sand: #EFE7D6;           /* warm sand panel (Kalyani) */
    --ink-panel: #14130F;      /* dark bento cards */
    --ink: #16150F;            /* headings (warm near-black) */
    --ink-soft: #3A372F;
    --body: #4A4742;           /* body text (>=4.5:1 on cream) */
    --mute: #6E6A5F;           /* captions / meta (5.08:1 cream, 4.78:1 paper) */
    --bamboo: #2F6B3F;         /* primary accent: CTA, links, marks */
    --bamboo-bright: #3E7C42;  /* large fills only */
    --bamboo-deep: #1E4D2B;    /* green text-safe on cream */
    --gold: #C8A24B;
    --hairline: rgba(22,21,15,0.10);
    --hairline-strong: rgba(22,21,15,0.16);

    /* Keyboard focus ring. Dark bespoke pages can rebind this on their own
       root container to keep the ring readable against their background. */
    --focus-ring: var(--bamboo-deep);

    /* Depth */
    --radius-card: 18px;
    --radius-pill: 999px;
    --shadow-warm: 0 1px 2px rgba(22,21,15,.05), 0 14px 36px rgba(22,21,15,.08);
    --shadow-warm-hover: 0 2px 6px rgba(22,21,15,.07), 0 26px 60px rgba(22,21,15,.13);
    --ease: cubic-bezier(.2,.8,.2,1);

    /* Type */
    --font-display: 'Bricolage Grotesque', 'Hanken Grotesk', -apple-system, BlinkMacSystemFont, sans-serif;
    --font-body: 'Switzer', 'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    --font-mono: 'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace;

  }

  * { margin: 0; padding: 0; box-sizing: border-box; }

  /* No global smooth scroll: route changes must be instant (the ScrollManager
     owns scroll). Smooth here would animate every navigation. */

  /* The ScrollManager focuses <main> on every route change; that is a landmark
     hand-off, not a control, so it never draws a ring. */
  main[tabindex]:focus,
  main[tabindex]:focus-visible { outline: none; }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .skip-link {
    position: fixed;
    top: 12px;
    left: 12px;
    z-index: 10000;
    transform: translateY(-150%);
    visibility: hidden; /* the shadow otherwise bleeds into the viewport while parked offscreen */
    background: var(--bamboo);
    color: #fff;
    font-family: var(--font-body);
    font-weight: 600;
    padding: 0.7rem 1.2rem;
    border-radius: 10px;
    box-shadow: 0 8px 22px rgba(22,21,15,0.18);
    transition: transform .2s ease;
  }
  .skip-link:focus-visible { transform: translateY(0); visibility: visible; outline: 2px solid var(--ink); }

  body {
    font-family: var(--font-body);
    color: var(--body);
    background-color: var(--cream);
    line-height: 1.65;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  /* Subtle paper grain over the whole site. multiply makes it vanish on
     dark bespoke heroes, and tints cream sections just enough to feel printed. */
  body::after {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 9998;
    opacity: 0.035;
    mix-blend-mode: multiply;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  }

  a { text-decoration: none; color: inherit; transition: color .2s ease; }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-display);
    color: var(--ink);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 600;
  }

  p { margin-bottom: 1rem; }

  img { max-width: 100%; height: auto; }

  /* Keyboard focus is visible everywhere; pointer interaction stays clean. */
  button:focus-visible,
  a:focus-visible,
  [role="button"]:focus-visible,
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible,
  summary:focus-visible,
  [tabindex]:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  button:focus:not(:focus-visible) { outline: none; }

  button {
    cursor: pointer;
    border: none;
    font-family: var(--font-body);
    background: var(--bamboo);
    color: #fff;
    padding: 0.75rem 1.5rem;
    border-radius: 10px;
    font-weight: 600;
    transition: background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;
  }

  section { padding: 5.5rem 0; @media (max-width: 768px){ padding: 3.5rem 0; } }

  .container { width: 90%; max-width: 1200px; margin: 0 auto; }

  ::selection { background: rgba(47,107,63,0.18); }

  @keyframes pt-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-14px); }
  }

  @media (prefers-reduced-motion: reduce) {
    html { scroll-behavior: auto; }
    *, *::before, *::after { animation-duration: .001ms !important; animation-iteration-count: 1 !important; transition-duration: .001ms !important; }
  }
`;

export default GlobalStyles;
